import { useEffect, useState } from "react";
import { useTenant } from "@/store/tenant";
import { Loader } from "@/components/loader";
import { Plus, Globe, Check, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { InputField } from "@/components/ui/InputField";
import { toast } from "sonner";

interface TenantDomainsTabProps {
  tenantId: string;
}

export const TenantDomainsTab = ({ tenantId }: TenantDomainsTabProps) => {
  const tenantStore = useTenant(tenantId);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // Form state
  const [domainId, setDomainId] = useState("");
  const [domainName, setDomainName] = useState("");
  const [isVerified, setIsVerified] = useState(false);

  const domains = tenantStore.domains.data;

  useEffect(() => {
    const loadData = async () => {
      try {
        await tenantStore.domains.list();
      } catch (error) {
        console.error("Failed to load Domains:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [tenantId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!domainName) {
      toast.error("Please enter a domain name");
      return;
    }

    setIsSubmitting(true);
    try {
      if (domainId) {
        await tenantStore.domains.update(domainId, {
          domain: domainName,
          is_verified: isVerified,
        });
        toast.success("Domain updated successfully");
      } else {
        await tenantStore.domains.create(domainName, isVerified);
        toast.success("Domain added successfully");
      }
      setIsModalOpen(false);
      resetForm();
    } catch (error: any) {
      toast.error(error.message || "Failed to configure domain");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    setIsSubmitting(true);
    try {
      await tenantStore.domains.delete(id);
      toast.success("Domain removed successfully");
      setIsModalOpen(false);
      resetForm();
    } catch (error: any) {
      toast.error(error.message || "Failed to remove domain");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setDomainId("");
    setDomainName("");
    setIsVerified(false);
    setShowDeleteConfirm(false);
    setDeleteConfirmText("");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader />
      </div>
    );
  }

  const isEditing = !!domainId;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
            Domains
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Manage domains associated with this tenant.
          </p>
        </div>
        <div className="flex gap-3 items-center">
          <Button
            onClick={() => {
              resetForm();
              setIsModalOpen(true);
            }}
            className="bg-orange-500 hover:bg-orange-600 text-white shadow-md border-0 gap-2 px-4 rounded-lg"
          >
            <Plus className="w-4 h-4" /> Add Domain
          </Button>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-950 border border-slate-100 dark:border-slate-900 rounded-2xl overflow-hidden">
        {domains.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest border-b border-slate-100 dark:border-slate-900">
                  <th className="px-6 py-3">Domain</th>
                  <th className="px-6 py-3">Verification Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100/60 dark:divide-slate-800/60">
                {domains.map((domainConfig, idx) => (
                  <tr
                    key={idx}
                    className="hover:bg-slate-50/50 dark:hover:bg-slate-900/50 transition-all"
                  >
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 border border-slate-200/60 shadow bg-white rounded-lg flex items-center justify-center overflow-hidden shrink-0">
                          <Globe className="w-5 h-5 text-slate-400" />
                        </div>
                        <div>
                          <button
                            onClick={() => {
                              setDomainId(domainConfig.id);
                              setDomainName(domainConfig.domain);
                              setIsVerified(domainConfig.is_verified);
                              setIsModalOpen(true);
                            }}
                            className="text-[13px] font-bold text-slate-900 dark:text-white hover:text-orange-500 text-left transition-colors"
                          >
                            {domainConfig.domain}
                          </button>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-3">
                      {domainConfig.is_verified ? (
                        <span className="px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400 flex items-center gap-1.5 w-max">
                          <Check className="w-3 h-3" />
                          Verified
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400 flex items-center gap-1.5 w-max">
                          <AlertTriangle className="w-3 h-3" />
                          Unverified
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-12 text-center">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Globe className="w-8 h-8 text-slate-400" />
            </div>
            <p className="text-slate-500 dark:text-slate-400">
              No domains configured
            </p>
            <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
              Add domains to this tenant
            </p>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center z-50 p-4 transition-all animate-in fade-in">
          <Card
            className="max-w-md w-full shadow-2xl scale-in-center overflow-hidden"
            title={isEditing ? "Edit Domain" : "Add Domain"}
            subtitle="Configure domain for this tenant."
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4 pt-2">
                <InputField
                  label="Domain Name"
                  placeholder="e.g. example.com"
                  value={domainName}
                  onChange={(e) => setDomainName(e.target.value)}
                  helpText="The fully qualified domain name."
                />

                <label className="flex items-center space-x-2 text-sm cursor-pointer mt-4">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300 text-orange-500 focus:ring-orange-500/50 dark:border-slate-600 dark:bg-slate-800"
                    checked={isVerified}
                    onChange={(e) => setIsVerified(e.target.checked)}
                  />
                  <span className="text-slate-600 dark:text-slate-400">Mark as Verified</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-slate-100 dark:border-slate-800">
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!domainName}
                  isLoading={isSubmitting}
                  className="gap-2"
                >
                  <Check className="w-4 h-4" />
                  {isEditing ? "Save Configuration" : "Add Domain"}
                </Button>
              </div>

              {isEditing && (
                <div className="border border-red-200/60 dark:border-red-900/30 bg-red-50/50 dark:bg-red-900/10 rounded-2xl overflow-hidden shadow-sm mt-6">
                  <div className="px-6 py-4 flex items-center gap-2 border-b border-red-100/60 dark:border-red-900/30">
                    <AlertTriangle className="w-4.5 h-4.5 text-red-500" />
                    <h3 className="font-bold text-red-600 dark:text-red-400 text-[15px]">
                      Danger Zone
                    </h3>
                  </div>
                  <div className="px-6 py-5 flex flex-col gap-4">
                    <div>
                      <h4 className="font-bold text-slate-900 dark:text-red-100 text-[13px]">
                        Remove Domain
                      </h4>
                      <p className="text-xs text-slate-600 dark:text-red-300 mt-1">
                        Once you remove a domain, users will no longer be able to log in using this domain.
                      </p>
                    </div>
                    {!showDeleteConfirm ? (
                      <div>
                        <Button
                          variant="danger"
                          type="button"
                          className="font-bold px-5 bg-red-600 hover:bg-red-700 rounded-full text-[13px]"
                          onClick={() => setShowDeleteConfirm(true)}
                        >
                          Remove Domain
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-3">
                        <div>
                          <p className="text-xs text-red-600 dark:text-red-400 font-medium mb-1.5">
                            Type <span className="font-bold">"{domainName}"</span> to confirm
                          </p>
                          <input
                            type="text"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            className="w-full px-3 py-1.5 text-sm border border-red-200 dark:border-red-800 rounded-md bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500/50"
                            placeholder={domainName}
                          />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="ghost"
                            type="button"
                            className="px-3 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 text-xs h-8 bg-transparent"
                            onClick={() => {
                              setShowDeleteConfirm(false);
                              setDeleteConfirmText("");
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="danger"
                            type="button"
                            className="font-bold px-4 bg-red-600 hover:bg-red-700 rounded-md text-xs h-8 border-0"
                            disabled={deleteConfirmText !== domainName || isSubmitting}
                            onClick={() => handleDelete(domainId)}
                          >
                            Confirm Delete
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};
